import Tooltip from '@mui/material/Tooltip';
import { useAtomValue } from 'jotai';
import { useEffect, useRef, useState } from 'react';

import { useDrawingManagerContext } from '#components/cad-renderer/context/drawing-manager-context.js';
import { useZIndexContext } from '#components/cad-renderer/context/z-index-context.js';
import { hoverInfoEnabledAtom } from './hover-info-atom.js';
import { MODULE_CLICK_POPOVER_OFFSET_PX, ModuleClickPopover } from './module-click-popover.js';
import { ModuleMiniCard } from './module-mini-card.js';
import { useCanvasAnchoredPosition } from './use-canvas-anchored-position.js';

import type { DrawingElement } from '@cadstrom/renderer';
import type { ReactNode } from 'react';
import type { CanvasAnchor, CanvasPoint } from './use-canvas-anchored-position.js';

/** A press is treated as a drag (and opens no popover) beyond this pointer travel (px). */
const PRESS_DRAG_THRESHOLD_PX = 4;

interface PinnedModule extends CanvasAnchor {
	moduleReferenceDesignator: string;
}

interface RendererPointerData {
	button: number;
	/** The pointer that pressed, the same identifier the DOM pointer events carry. */
	pointerId: number;
	/** The press position in canvas-local coordinates. */
	global: { x: number; y: number };
	originalEvent?: { ctrlKey?: boolean };
}

/**
 * Whether a renderer pointer press is a right-click. The renderer reads ctrl+left as the right
 * button, so both spellings count.
 *
 * @param data - The pointer data of the renderer's `elementPointerDown` event.
 * @returns `true` for the right button or a ctrl+left press.
 */
function isRightClick(data: RendererPointerData): boolean {
	return data.button === 2 || (data.button === 0 && data.originalEvent?.ctrlKey === true);
}

export interface ModuleHoverInfoProps {
	/** Whether this renderer instance is the currently visible sheet. */
	visible: boolean;
	className?: string;
	/** The canvas area the hover tooltip tracks. */
	children: ReactNode;
}

/**
 * Wraps the renderer canvas area with module hover info: a cursor-following, non-interactive
 * tooltip showing a miniature module card while a module is hovered, which pins into the full,
 * interactive module info popover at the pointer position when the module is right-clicked (or
 * ctrl+clicked, which the renderer reads as a right-click). A left click only selects, and a
 * right-drag still pans.
 *
 * The hover tooltip honours the toolbar's hover-info toggle; the right-click-to-pin popover always
 * works.
 */
export function ModuleHoverInfo({ visible, className, children }: ModuleHoverInfoProps) {
	const { drawingManager } = useDrawingManagerContext();
	const hoverInfoEnabled = useAtomValue(hoverInfoEnabledAtom);
	// Read-only: this component mounts in the same pass as its ZIndexProvider, so it reads the
	// layer's z-index rather than pushing onto the stack.
	const zIndex = useZIndexContext(false)?.getZIndex('context-menu');

	// Most-recently-hovered first; overlapping elements keep the stack non-empty until every one
	// un-hovers, mirroring the status bar's hover tracking.
	const [hoveredModules, setHoveredModules] = useState<DrawingElement[]>([]);
	const [pinned, setPinned] = useState<PinnedModule | null>(null);
	const [canvasArea, setCanvasArea] = useState<HTMLDivElement | null>(null);
	// The pin follows the drawing as the viewport pans and zooms, and is dropped once it leaves it.
	const pinnedPosition = useCanvasAnchoredPosition(drawingManager, canvasArea, pinned);

	/** The press in progress over the canvas area: which pointer started it, and where. */
	const pressRef = useRef<{ pointerId: number; position: CanvasPoint } | null>(null);
	const pendingPressRef = useRef<PinnedModule | null>(null);
	/** Whether the latest pointer-up pinned the popover, so a DOM click trailing the press is claimed. */
	const pinnedOnPointerUpRef = useRef(false);
	/** The native event of the click that trailed a (re-)pin, so the popover's own click-away ignores it. */
	const lastPinEventRef = useRef<Event | null>(null);

	// A hidden sheet cannot keep a pinned popover: the popover portals to the document body, so it
	// would outlive its sheet visually. Its hover stack goes stale for the same reason the canvas
	// stops emitting — nothing un-hovers what sat under the cursor when the sheet was hidden, so a
	// return to this sheet would re-open the tooltip away from the pointer. Reset during render
	// rather than in an effect.
	if (!visible) {
		if (pinned !== null) {
			setPinned(null);
		}
		if (hoveredModules.length > 0) {
			setHoveredModules([]);
		}
	} else if (pinned !== null && pinnedPosition === null) {
		setPinned(null);
	}

	useEffect(() => {
		if (!drawingManager) {
			return;
		}

		const handleHoverStateChanged = (element: DrawingElement) => {
			if (element.modelObjectType !== 'modules') {
				return;
			}
			setHoveredModules((current) => {
				const without = current.filter((e) => e !== element);
				return element.isHoveredIrregardlessOfSelection && element.isVisible ? [element, ...without] : without;
			});
		};

		drawingManager.on('elementHoverStateChanged', handleHoverStateChanged);
		return () => {
			drawingManager.removeListener('elementHoverStateChanged', handleHoverStateChanged);
			setHoveredModules([]);
		};
	}, [drawingManager]);

	useEffect(() => {
		if (!drawingManager) {
			return;
		}

		// Pointer-down only records the candidate; the popover opens on the pointer-up that ends the
		// press, since a right button never produces a DOM click. Another pointer pressing meanwhile
		// is a press of its own, and must not replace the candidate this one is waiting on.
		const handleElementPointerDown = (event: { data: RendererPointerData }, element: DrawingElement) => {
			const press = pressRef.current;
			if (
				!isRightClick(event.data) ||
				element.modelObjectType !== 'modules' ||
				typeof element.objectId !== 'string' ||
				press === null ||
				event.data.pointerId !== press.pointerId
			) {
				return;
			}
			const world = drawingManager.renderer.getViewport().toWorld(event.data.global.x, event.data.global.y);
			pendingPressRef.current = {
				moduleReferenceDesignator: element.objectId,
				position: press.position,
				world: { x: world.x, y: world.y },
			};
		};

		drawingManager.on('elementPointerDown', handleElementPointerDown);
		return () => {
			drawingManager.removeListener('elementPointerDown', handleElementPointerDown);
			pendingPressRef.current = null;
		};
	}, [drawingManager]);

	useEffect(() => {
		/**
		 * Ends the press that the released pointer started, discarding its candidate.
		 *
		 * A press released off the canvas area never reaches the wrapper's own pointer-up, which would
		 * otherwise leave the candidate for an unrelated later release to pin; one released over the
		 * canvas has already been handled there by the time it reaches the window. A release from any
		 * other pointer is not this press ending, so a second finger cannot cancel it.
		 *
		 * @param event - A pointer-up or pointer-cancel from anywhere in the window.
		 */
		const endPress = (event: PointerEvent) => {
			if (event.pointerId !== pressRef.current?.pointerId) {
				return;
			}
			pressRef.current = null;
			pendingPressRef.current = null;
		};

		window.addEventListener('pointerup', endPress);
		window.addEventListener('pointercancel', endPress);
		return () => {
			window.removeEventListener('pointerup', endPress);
			window.removeEventListener('pointercancel', endPress);
			pressRef.current = null;
			pendingPressRef.current = null;
		};
	}, []);

	/**
	 * Records where the press started and discards any candidate left by the previous press. A
	 * pointer joining a press already in flight starts no press of its own, so the waiting one keeps
	 * its candidate until its own pointer is released.
	 *
	 * @param event - The pointer-down event over the canvas area.
	 */
	const handlePointerDownCapture = (event: React.PointerEvent) => {
		if (pressRef.current !== null && pressRef.current.pointerId !== event.pointerId) {
			return;
		}
		pressRef.current = { pointerId: event.pointerId, position: { x: event.clientX, y: event.clientY } };
		pendingPressRef.current = null;
		pinnedOnPointerUpRef.current = false;
	};

	/**
	 * Pins the module info popover when the press that just ended was a right-click on a module and
	 * the pointer stayed put; a press that travelled is a right-drag pan and pins nothing. A release
	 * from any other pointer is not that press ending, and leaves it waiting.
	 *
	 * @param event - The pointer-up event over the canvas area.
	 */
	const handlePointerUpCapture = (event: React.PointerEvent) => {
		if (event.pointerId !== pressRef.current?.pointerId) {
			return;
		}
		const pending = pendingPressRef.current;
		pendingPressRef.current = null;
		if (pending === null) {
			return;
		}
		// Travel beyond the threshold is a right-drag pan, not a click.
		if (
			Math.abs(event.clientX - pending.position.x) > PRESS_DRAG_THRESHOLD_PX ||
			Math.abs(event.clientY - pending.position.y) > PRESS_DRAG_THRESHOLD_PX
		) {
			return;
		}
		pinnedOnPointerUpRef.current = true;
		setPinned(pending);
	};

	/**
	 * Claims the DOM click that trails a ctrl+left pin, so the popover's click-away listener does
	 * not read that click as an outside click and close what it just opened.
	 *
	 * @param event - The click event over the canvas area.
	 */
	const handleClickCapture = (event: React.MouseEvent) => {
		if (!pinnedOnPointerUpRef.current) {
			return;
		}
		pinnedOnPointerUpRef.current = false;
		lastPinEventRef.current = event.nativeEvent;
	};

	/**
	 * Closes the pinned popover, except when the closing event is the very click that pinned it —
	 * ctrl+clicking another module re-pins on pointer-up, and the trailing click would otherwise
	 * dismiss the new pin.
	 *
	 * @param event - The event that requested the close, when one exists.
	 */
	const handlePopoverClose = (event?: MouseEvent | TouchEvent | React.KeyboardEvent) => {
		if (event !== undefined && event === lastPinEventRef.current) {
			return;
		}
		setPinned(null);
	};

	const topHoveredObjectId = hoveredModules[0]?.objectId;
	const hoveredModuleName = typeof topHoveredObjectId === 'string' ? topHoveredObjectId : undefined;
	const tooltipOpen = hoverInfoEnabled && pinned === null && hoveredModuleName !== undefined;

	return (
		<>
			<Tooltip
				open={tooltipOpen}
				title={hoveredModuleName !== undefined ? <ModuleMiniCard moduleReferenceDesignator={hoveredModuleName} /> : ''}
				followCursor
				placement='bottom-start'
				disableInteractive
				disableHoverListener
				disableFocusListener
				disableTouchListener
				slotProps={{
					popper: {
						style: { zIndex },
						modifiers: [{ name: 'offset', options: { offset: [0, MODULE_CLICK_POPOVER_OFFSET_PX] } }],
					},
					transition: { timeout: 0 },
					tooltip: {
						className: 'm-0 rounded-md border border-gray-a5 bg-gray-2 px-2.5 py-1.5 text-gray-12 shadow-lg',
					},
				}}
			>
				<div
					ref={setCanvasArea}
					className={className}
					onPointerDownCapture={handlePointerDownCapture}
					onPointerUpCapture={handlePointerUpCapture}
					onClickCapture={handleClickCapture}
				>
					{children}
				</div>
			</Tooltip>

			{pinned !== null && pinnedPosition !== null && (
				<ModuleClickPopover
					key={pinned.moduleReferenceDesignator}
					moduleReferenceDesignator={pinned.moduleReferenceDesignator}
					position={pinnedPosition}
					onClose={handlePopoverClose}
				/>
			)}
		</>
	);
}
